import React, { Suspense, useState } from "react";
import { Alert, AppBar, Box, CircularProgress, Dialog, DialogContent, IconButton, Snackbar, Toolbar, Typography } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { getGlobalNavigation, getMobileNavigation } from "../navigation/NavigationRegistry";
import { getNavIcon } from "./navigation/iconMap";
import CloseIcon from "@mui/icons-material/Close";
import { colors, layout, radii, shadows, typography } from "../design/tokens";

const NewRequestForm = React.lazy(() => import("./NewRequestForm"));

const ACCENT = colors.primary;
const MUTED = colors.textMuted;

// Phase 3 — Navigation Architecture (Step 9: Mobile Navigation)
//
// Bottom navigation is generated from the registry's prayer-first mobile
// projection: Home, Circles, Share, Prayer and Inbox.
const CENTER_ID = "share";

function isActive(item, location) {
  if (item.matchRoutes) {
    return item.matchRoutes.some((route) => location.pathname.startsWith(route));
  }
  if (item.matchSearch) {
    return location.pathname === "/" && location.search.includes(item.matchSearch);
  }
  if (item.route === "/") return location.pathname === "/";
  return location.pathname.startsWith(item.route) && item.route !== "/";
}

export default function MobileBottomNav() {
  const location = useLocation();
  const [composerOpen, setComposerOpen] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");

  const mobileItems = getMobileNavigation(getGlobalNavigation());
  const centerItem = mobileItems.find((item) => item.id === CENTER_ID);
  const sideItems = mobileItems.filter((item) => item.id !== CENTER_ID);
  const middle = Math.ceil(sideItems.length / 2);
  const tabs = [
    ...sideItems.slice(0, middle),
    ...(centerItem ? [{ ...centerItem, isCenter: true }] : []),
    ...sideItems.slice(middle),
  ];

  return (
    <>
      <Box
        component="nav"
        aria-label="Primary navigation"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          maxWidth: "100vw",
          minWidth: 0,
          overflowX: "hidden",
          minHeight: 64,
          backgroundColor: colors.surface,
          borderTop: `1px solid ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          zIndex: 1200,
          pb: "env(safe-area-inset-bottom)",
        }}
      >
      {tabs.map((tab) => {
        const Icon = getNavIcon(tab.icon);
        const active = isActive(tab, location);

        if (tab.isCenter) {
          return (
            <Box
              key={tab.id}
              component="button"
              type="button"
              onClick={() => setComposerOpen(true)}
              aria-label="Share a prayer or praise"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 64,
                p: 0,
                border: 0,
                background: "transparent",
                color: ACCENT,
                cursor: "pointer",
              }}
            >
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: `${radii.pill}px`,
                backgroundColor: ACCENT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: shadows.raised,
                transform: "translateY(-8px)",
              }}>
                <Icon sx={{ fontSize: 24, color: colors.surface }} />
              </Box>
              <Box component="span" sx={{ mt: "-6px", fontSize: 10, fontFamily: typography.sans, fontWeight: 700 }}>
                {tab.label}
              </Box>
            </Box>
          );
        }

        return (
          <Box
            key={tab.id}
            component={NavLink}
            to={tab.route}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textDecoration: "none",
              gap: "2px",
              flex: "1 1 0",
              minWidth: 0,
              minHeight: 64,
              justifyContent: "center",
            }}
            aria-current={active ? "page" : undefined}
          >
            <Icon sx={{ fontSize: 22, color: active ? ACCENT : MUTED }} />
            <Box component="span" sx={{
              fontSize: "10px",
              fontFamily: typography.sans,
              fontWeight: active ? 600 : 400,
              color: active ? ACCENT : MUTED,
              lineHeight: 1,
            }}>
              {tab.label}
            </Box>
            {active && (
              <Box sx={{
                width: 4, height: 4, borderRadius: "50%",
                backgroundColor: ACCENT, mt: "1px",
              }} />
            )}
          </Box>
        );
      })}
      </Box>

      <Dialog fullScreen open={composerOpen} onClose={() => setComposerOpen(false)}>
        <AppBar position="sticky" color="inherit" elevation={0}>
          <Toolbar sx={{ minHeight: 56, gap: 1 }}>
            <IconButton edge="start" onClick={() => setComposerOpen(false)} aria-label="Close prayer composer">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ fontFamily: typography.serif, fontSize: "1.25rem", color: colors.text }}>
              Share a prayer or praise
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ px: 2, py: 2.5, backgroundColor: colors.canvas }}>
          <Box sx={{ width: "100%", maxWidth: layout.feedMaxWidth, mx: "auto" }}>
            <Suspense
              fallback={(
                <Box role="status" sx={{ minHeight: 160, display: "grid", placeItems: "center" }}>
                  <CircularProgress size={28} aria-label="Loading prayer composer" />
                </Box>
              )}
            >
              <NewRequestForm
                onSuccess={({ audienceLabel }) => {
                  setComposerOpen(false);
                  setShareFeedback(`Shared with ${audienceLabel}.`);
                }}
              />
            </Suspense>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(shareFeedback)}
        autoHideDuration={4000}
        onClose={() => setShareFeedback("")}
      >
        <Alert severity="success" variant="filled" onClose={() => setShareFeedback("")}>
          {shareFeedback}
        </Alert>
      </Snackbar>
    </>
  );
}
